import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import CustomDialog from "../../widgets/Dialog/Dialog";
import SecondaryButton from "../../widgets/Button/SecondaryButton/SecondaryButton";
import {
  useFetchCustomerInfo,
  useGenerateOtp,
  useUnblockPhoneNumber,
} from "../../hooks/api";
import SingleSelect from "../../widgets/Dropdown/SingleSelectDropdown";
import {
  PRODUCT_LIST,
  PRODUCT_TENANT_MAPPING,
  VERTICALS,
} from "../../constants/constants";
import PrimaryButton from "../../widgets/Button/PrimaryButton/PrimaryButton";
import { useSnackbarContext } from "../../widgets/CustomSnackBar/CustomSnackBar";
import { useUnblockUser } from "../../store/backend/useUnblockuser";
import "./SearchUserContainer.css";

const handleSearch = async (
  uamId,
  selectedBusinessVertical,
  fetchCustomerInfo,
  navigate,
  openSnackbar
) => {
  const payload = { selectedBusinessVertical, uam_id: uamId };
  fetchCustomerInfo(payload, {
    onSuccess: (customerDetails) => {
      if (!Array.isArray(customerDetails)) {
        openSnackbar("Unexpected response format.", "error");
        return;
      }
      const selectedUser = customerDetails[0];
      navigate(
        `/userdetails/UAM?uamId=${selectedUser.uamId}&verticalId=${selectedBusinessVertical}`
      );
    },
    onError: (error) => {
      openSnackbar(error.message || "Failed to fetch customer info.", "error");
    },
  });
};

const handleUnblockUser = (
  admissionNumber,
  unblockUserProductId,
  openSnackbar,
  unblockUser
) => {
  const tenantId = PRODUCT_TENANT_MAPPING[unblockUserProductId];
  unblockUser(
    {
      admission_id: admissionNumber,
      product_id: tenantId.product_id,
      tenant_id: tenantId.tenant_id,
    },
    {
      onSuccess: (data) => {
        openSnackbar(data.success || "User unblocked successfully", "success");
      },
      onError: (error) => {
        openSnackbar(error.message || "Failed to unblock user", "error");
      },
    }
  );
};

const handleOnClickResetOtpButton = (
  phoneNumber,
  unblockPhoneNumber,
  openSnackbar
) => {
  unblockPhoneNumber(phoneNumber, {
    onSuccess: (data) => {
      openSnackbar(data.success || "OTP reset successful", "success");
    },
    onError: (error) => {
      openSnackbar(error.message || "Failed to reset OTP", "error");
    },
  });
};

const handleOnClickGetOtpButton = (
  generateOtp,
  phoneNumber,
  admissionNumber,
  selectedProduct,
  openSnackbar,
  setOtp
) => {
  if (!phoneNumber && !admissionNumber) {
    openSnackbar(
      "Please enter either phone number or admission number.",
      "error"
    );
    return;
  }

  const mapping = PRODUCT_TENANT_MAPPING[selectedProduct];
  if (!mapping) {
    openSnackbar("Invalid product selected.", "error");
    return;
  }

  const { product_id, tenant_id } = mapping;

  const payload = {
    productId: product_id,
    tenantId: tenant_id,
    ...(phoneNumber ? { phoneNumber } : {}),
    ...(admissionNumber ? { admissionNumber } : {}),
  };

  generateOtp(payload, {
    onSuccess: (data) => {
      openSnackbar(
        `Fetched OTP successfully for ${phoneNumber || admissionNumber}`,
        "success"
      );

      const code = data?.otp_code || data?.message || data;
      setOtp(code);

      if (data?.message) {
        openSnackbar(data.message, "error");
      }
    },

    onError: (error) => {
      openSnackbar(error.message || "Failed to fetch OTP", "error");
    },
  });
};
const shouldDisableGetOtpButton = (
  phoneNumber,
  admissionNumber,
  selectedProduct
) => {
  const isPhoneInvalid = !phoneNumber || phoneNumber.length < 10;
  const isProductNotSelected = !selectedProduct;
  const isAdmissionNumberEmpty = !admissionNumber;

  if (!isAdmissionNumberEmpty) {
    return false;
  }

  return isPhoneInvalid || isProductNotSelected;
};

const shouldDisableUnblockUserButton = (admissionNumber, selectedProduct) => {
  const isAdmissionNumberEmpty = !admissionNumber;
  const isProductNotSelected = !selectedProduct;

  return isAdmissionNumberEmpty || isProductNotSelected;
};
const shouldDisableSearchUserButton = (uamId, selectedBusinessVertical) => {
  const isUamIdEmpty = !uamId;
  const isBusinessVerticalNotSelected = !selectedBusinessVertical;

  return isUamIdEmpty || isBusinessVerticalNotSelected;
};

const SearchUserContainer = () => {
  const navigate = useNavigate();
  const { openSnackbar } = useSnackbarContext();
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [unblockUserProductId, setUnblockUserProductId] = useState("");
  const [admissionNumber, setAdmissionNumber] = useState("");
  const [phoneNumberToGetOtp, setPhoneNumberToGetOtp] = useState("");
  const [otp, setOtp] = useState("");
  const [selectedBusinessVertical, setSelectedBusinessVertical] = useState("");
  const [uamId, setUamId] = useState("");
  const [admissionNoToUnblockUser, setAdmissionNoToUnblockUser] = useState("");
  const {
    mutate: fetchCustomerInfo,
    isPending: isfetchCustomerInformationLoading,
  } = useFetchCustomerInfo();

  const { mutate: unblockPhoneNumber, isLoading: isUnblockPhoneNumberLoading } =
    useUnblockPhoneNumber();
  const { mutate: generateOtp, isLoading: isGenerateOtpLoading } =
    useGenerateOtp();

  const handleCloseErrorPopup = () => setShowErrorPopup(false);
  const {
    mutate: unblockUser,
    isLoading: isUnblockUserLoading,
    error: unblockUserError,
  } = useUnblockUser();

  return (
    <>
      <div className='search-user-main-container '>
        <div className='search-user-container '>
          <h1 className='search-user-title'>Search User</h1>
          <div className='flex-container'>
            <SingleSelect
              menuItems={VERTICALS}
              placeholder='Select a vertical'
              handleChange={(event) =>
                setSelectedBusinessVertical(event.target.value)
              }
              selectedValue={selectedBusinessVertical}
              value={selectedBusinessVertical || ""}
            />
            <input
              className='search-input'
              type='text'
              value={uamId}
              onChange={(event) => setUamId(event.target.value)}
              placeholder='Enter UAM ID'
              disabled={loading}
            />

            <PrimaryButton
              className='search-button'
              onClick={() =>
                handleSearch(
                  uamId,
                  selectedBusinessVertical,
                  fetchCustomerInfo,
                  navigate,
                  openSnackbar
                )
              }
              disabled={
                shouldDisableSearchUserButton(
                  uamId,
                  selectedBusinessVertical
                ) || isfetchCustomerInformationLoading
              }
            >
              {isfetchCustomerInformationLoading ? "Searching..." : "Search"}
            </PrimaryButton>
          </div>
        </div>
        <div className='reset-otp-container '>
          <h1 className='reset-otp-title'>Reset Otp</h1>
          <div className='search-inputs'>
            <input
              className='search-input'
              type='tel'
              value={phoneNumber}
              onChange={(event) => {
                const inputVal = event.target.value.replace(/\D/g, "");
                setPhoneNumber(inputVal.slice(0, 10));
              }}
              placeholder='Enter a phone number'
            />
            <PrimaryButton
              className='reset-otp-button'
              onClick={() =>
                handleOnClickResetOtpButton(
                  phoneNumber,
                  unblockPhoneNumber,
                  openSnackbar
                )
              }
              disabled={!phoneNumber || isUnblockPhoneNumberLoading}
            >
              {isUnblockPhoneNumberLoading ? "Loading" : "Reset Otp"}
            </PrimaryButton>
          </div>
        </div>
        <div className='get-otp-container '>
          <h1 className='get-otp-title'>Get Otp</h1>
          <div className='flex-container'>
            <SingleSelect
              menuItems={PRODUCT_LIST}
              placeholder='Select a product'
              handleChange={(event) => setSelectedProduct(event.target.value)}
              value={selectedProduct || ""}
            />

            <input
              className='search-input'
              type='text'
              value={phoneNumberToGetOtp}
              onChange={(event) => {
                const inputVal = event.target.value.replace(/\D/g, "");
                setPhoneNumberToGetOtp(inputVal.slice(0, 10));
                setOtp("");
              }}
              placeholder='Enter a phone number'
              disabled={loading}
            />
            <input
              className='search-input'
              type='text'
              value={admissionNumber}
              onChange={(event) => {
                setAdmissionNumber(event.target.value);
                setOtp("");
              }}
              placeholder='Enter admission number'
              disabled={loading}
            />
          </div>
          <div className='button-container'>
            <PrimaryButton
              className='get-otp-button'
              onClick={() =>
                handleOnClickGetOtpButton(
                  generateOtp,
                  phoneNumberToGetOtp,
                  admissionNumber,
                  selectedProduct,
                  openSnackbar,
                  setOtp
                )
              }
              disabled={shouldDisableGetOtpButton(
                phoneNumberToGetOtp,
                admissionNumber,
                selectedProduct
              )}
            >
              {isGenerateOtpLoading ? "Loading..." : "Get OTP"}
            </PrimaryButton>
          </div>
          {otp && (
            <div className='otp-display'>
              <p>OTP: {otp.message || otp}</p>
            </div>
          )}
        </div>
        <div className='unblock-user-container '>
          <h1 className='unblock-user-title'>Unblock User's Password</h1>
          <div className='flex-container'>
            <SingleSelect
              menuItems={PRODUCT_LIST}
              placeholder='Select a product'
              handleChange={(event) =>
                setUnblockUserProductId(event.target.value)
              }
              value={unblockUserProductId || ""}
            />
            <input
              className='search-input'
              type='text'
              value={admissionNoToUnblockUser}
              onChange={(event) => {
                const inputVal = event.target.value;
                setAdmissionNoToUnblockUser(inputVal);
              }}
              placeholder='Enter Admission Number'
              disabled={loading}
            />
            <div className='unblock-button-container'>
              <PrimaryButton
                className='unblock-button'
                onClick={() =>
                  handleUnblockUser(
                    admissionNoToUnblockUser,
                    unblockUserProductId,
                    openSnackbar,
                    unblockUser,
                    setUnblockUserProductId,
                    setAdmissionNoToUnblockUser
                  )
                }
                disabled={
                  shouldDisableUnblockUserButton(
                    admissionNoToUnblockUser,
                    unblockUserProductId
                  ) || isUnblockUserLoading
                }
              >
                {isUnblockUserLoading ? "Unblocking..." : "Unblock User"}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>
      <CustomDialog
        isOpen={showErrorPopup}
        handleOnClose={handleCloseErrorPopup}
      >
        <div className='popup-content'>
          <p>User not found. Please try again.</p>
          <div className='button-container'>
            <SecondaryButton onClick={handleCloseErrorPopup}>
              Close
            </SecondaryButton>
          </div>
        </div>
      </CustomDialog>
    </>
  );
};

export default SearchUserContainer;
